/**
 * Theme switcher for the marketing site.
 *
 * Three themes — light / dark / high-contrast — applied by toggling
 * `data-theme` on <html> (the CSS-variable token sets live in src/themes.css;
 * see design-system/MASTER.md §10). The choice is persisted in `nv-theme`
 * (localStorage) and re-applied pre-paint by /public/theme-init.js (no FOUC).
 *
 * - Segmented, keyboard-navigable radio group (`role="radiogroup"` +
 *   `aria-checked`), matching the engineered Precision Blue aesthetic.
 * - Initial visible state is read from `document.documentElement.dataset.theme`
 *   on mount, so it always matches what theme-init.js already painted (no
 *   hydration mismatch — React does not own <html>).
 * - Updates <meta name="theme-color"> per theme so mobile browser chrome
 *   matches.
 * - Inline SVG icons (Sun / Moon / Contrast) — the project does not bundle an
 *   icon library; this mirrors the existing inline-SVG convention in App.tsx.
 */

import { useCallback, useEffect, useId, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

type Theme = 'light' | 'dark' | 'hc';

const STORAGE_KEY = 'nv-theme';

/** Mobile browser-chrome color per theme (matches the surface ink panel). */
const META_THEME_COLOR: Record<Theme, string> = {
  light: '#0E1B2E',
  dark: '#0D1522',
  hc: '#000000',
};

interface ThemeSwitcherProps {
  /** `light` is tuned for dark backgrounds (e.g. the footer). */
  variant?: 'default' | 'light';
  className?: string;
}

function SunIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-4 h-4"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-4 h-4"
      aria-hidden="true"
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function ContrastIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-4 h-4"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 3a9 9 0 0 0 0 18z" fill="currentColor" stroke="none" />
    </svg>
  );
}

function readCurrentTheme(): Theme {
  if (typeof document === 'undefined') return 'light';
  const attr = document.documentElement.dataset.theme;
  return attr === 'dark' || attr === 'hc' || attr === 'light' ? attr : 'light';
}

function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* storage may be unavailable (private mode) — non-fatal */
  }
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', META_THEME_COLOR[theme]);
}

export function ThemeSwitcher({
  variant = 'default',
  className = '',
}: ThemeSwitcherProps) {
  const { t } = useTranslation();
  const groupId = useId();

  // Start at 'light' for the SSR/first render (matches the English `/` SSR
  // markup), then sync to whatever theme-init.js already applied to <html>.
  const [theme, setTheme] = useState<Theme>('light');

  useEffect(() => {
    setTheme(readCurrentTheme());
  }, []);

  const select = useCallback((next: Theme) => {
    applyTheme(next);
    setTheme(next);
  }, []);

  const options: { value: Theme; label: string; Icon: () => ReactElement }[] = [
    {
      value: 'light',
      label: t('common:theme.light', { defaultValue: 'Light' }),
      Icon: SunIcon,
    },
    {
      value: 'dark',
      label: t('common:theme.dark', { defaultValue: 'Dark' }),
      Icon: MoonIcon,
    },
    {
      value: 'hc',
      label: t('common:theme.highContrast', { defaultValue: 'High contrast' }),
      Icon: ContrastIcon,
    },
  ];

  const onDark = variant === 'light';

  // Container: a hairline-bordered segmented track that themes itself.
  const containerClasses = onDark
    ? 'border-hairline-dark bg-transparent'
    : 'border-hairline-strong bg-surface-raised';

  return (
    <div
      role="radiogroup"
      aria-label={t('common:theme.switcher', { defaultValue: 'Color theme' })}
      className={`inline-flex items-center gap-0.5 rounded-md border p-0.5 ${containerClasses} ${className}`}
    >
      {options.map(({ value, label, Icon }) => {
        const active = theme === value;
        const base =
          'inline-flex items-center justify-center rounded-sm p-1.5 text-sm transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-surface';
        const state = active
          ? 'bg-primary text-on-primary'
          : onDark
            ? 'text-footer-muted hover:text-ink-invert hover:bg-ink-surface2'
            : 'text-ink-muted hover:text-ink hover:bg-surface-sunken';
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={label}
            title={label}
            id={`${groupId}-${value}`}
            onClick={() => select(value)}
            className={`${base} ${state}`}
          >
            <Icon />
            <span className="sr-only">{label}</span>
          </button>
        );
      })}
    </div>
  );
}
